import { ReactComponent as IconChevronDown } from "@/assets/svgs/chevron-down.svg";
import { ReactComponent as IconInformationCircle } from "@/assets/svgs/information-circle-transparent.svg";
import Checkbox from "@/components/Checkbox/index";
import { setIsDirty } from "@/redux/actions/isDirty";
import { ListItemText, MenuItem, Select } from "@mui/material";
import {
	flexRender,
	getCoreRowModel,
	getExpandedRowModel,
	getFilteredRowModel,
	useReactTable
} from "@tanstack/react-table";
import PropTypes from 'prop-types';
import * as React from "react";
import { Fragment, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import Loader from 'react-loader-spinner';
import { useDispatch, useSelector } from "react-redux";
import Button from "../Button/index";
import Filter from "./Filter/index";
import FilterApplied from "./FilterApplied/index";
import SelectedHeader from "./SelectedHeader/index";


const EditCell = ({ type, render, onChange, columnId, onBlur, value, row, options, optionKey = 'value', optionValue = 'value', optionSelected = 'value' }) => {
	if (render) {
		return render()
	}
	else if (type === 'input') {
		return (
			<input
				data-testid="data-table3-edit-input"
				value={value}
				onChange={onChange}
				onBlur={onBlur}
			/>
		)
	} else if (type === 'multiSelect') {
		const originalValue = Array.isArray(value) ? value : value.split(',').map(s => s.trim())

		const handleMultiSelectOnChange = (e) => {
			const selectedValue = e.target.value;
			if (selectedValue[selectedValue.length - 1] === "all") {
				onChange({
					target: {
						value: originalValue.length === options.length ? [] : options.map(option => option?.[optionValue])
					}
				});
				return;
			}
			onChange({ target: { value: selectedValue } });
		}

		return (
			<Select
				data-testid="data-table3-edit-multi-select"
				value={originalValue}
				inputProps={{
					"aria-label": "data-table3-edit-multi-select"
				}}
				label="data-table3-edit-multi-select"
				style={{ width: '100%' }}
				classes={{
					root: 'data-table3__multi-select'
				}}
				renderValue={() => originalValue.join(', ')}
				onChange={handleMultiSelectOnChange}
				onBlur={onBlur}
				multiple
				sx={{ '& legend': { display: 'none' }, '& fieldset': { top: 0 }, }}
			>
				{/* Select all */}
				<MenuItem
					data-testid="data-table3-edit-multi-select-option-all"
					inputProps={{
						"aria-label": "data-table3-edit-multi-select-option-all"
					}}
					key={'all'}
					value={'all'}
					classes={{
						root: ['data-table3__multi-select-option', (originalValue.length === options.length ? 'Mui-selected' : '')].join(' ')
					}}
				>
					<Checkbox selected={originalValue.length === options.length} />
					<ListItemText primary={'All'} />
				</MenuItem>
				{options.map(option => (
					<MenuItem
						data-testid="data-table3-edit-multi-select-option"
						inputProps={{
							"aria-label": "data-table3-edit-multi-select-option"
						}}
						key={option?.[optionKey]}
						value={option?.[optionValue]}
						classes={{
							root: 'data-table3__multi-select-option'
						}}
					>
						<Checkbox selected={originalValue.indexOf(option?.[optionSelected]) > -1} />
						<ListItemText primary={option?.[optionValue]} />
					</MenuItem>
				))}
			</Select>
		)
	} else if (type === 'singleSelect') {
		const originalValue = value;

		return (
			<Select
				data-testid="data-table3-edit-single-select"
				value={originalValue?.toUpperCase()}
				inputProps={{
					"aria-label": "data-table3-edit-single-select"
				}}
				style={{ width: '100%' }}
				classes={{
					root: 'data-table3__single-select'
				}}
				onChange={onChange}
				onBlur={onBlur}
				sx={{ '& legend': { display: 'none' }, '& fieldset': { top: 0 }, }}
			>
				{options.map(option => (
					<MenuItem
						data-testid="data-table3-edit-single-select-option"
						inputProps={{
							"aria-label": "data-table3-edit-single-select-option"
						}}
						key={option?.[optionKey]}
						value={option?.[optionSelected]?.toUpperCase()}
						classes={{
							root: 'data-table3__single-select-option'
						}}
					>
						<ListItemText primary={option?.[optionValue]} />
					</MenuItem>
				))}
			</Select>
		)
	} else {
		return <>{value}</>
	}
}

EditCell.propTypes = {
	type: PropTypes.oneOf(['input', 'multiSelect', 'singleSelect']),
	render: PropTypes.func,
	onChange: PropTypes.func,
	columnId: PropTypes.string,
	onBlur: PropTypes.func,
	value: PropTypes.any,
	row: PropTypes.object,
	options: PropTypes.array,
	optionKey: PropTypes.string,
	optionValue: PropTypes.string,
	optionSelected: PropTypes.string,
}

const Toolbar = ({ filter, setFilter, searchPlaceholder, showSelectedActions, showSearchInput, rowSelection, selectedHeaderActions, ...toolbarProps }) => {
	return (
		<>
			<Filter
				filter={filter}
				setFilter={setFilter}
				searchPlaceholder={searchPlaceholder}
				showSearchInput={showSearchInput}
				data-testid={'user-management-filter'}
				{...toolbarProps}
			/>
			<FilterApplied
				filter={filter}
				setFilter={setFilter}
			/>
			{showSelectedActions && (
				<SelectedHeader
					selectedList={Object.keys(rowSelection)}
					actions={selectedHeaderActions}
				/>
			)}
		</>
	)
};

Toolbar.propTypes = {
	filter: PropTypes.objectOf(
		PropTypes.shape({
			search: PropTypes.string,
		})
	).isRequired,
	setFilter: PropTypes.func.isRequired,
	searchPlaceholder: PropTypes.string,
	showSelectedActions: PropTypes.bool,
	showSearchInput: PropTypes.bool,
	rowSelection: PropTypes.object,
	selectedHeaderActions: PropTypes.node,
}

const defaultColumn = {
	cell: function Cell({ getValue, row, column: { id, columnDef }, table }) {
		const initialValue = getValue()
		const [value, setValue] = useState(table?.options?.meta?.changedValues?.[row.id]?.[id] ?? initialValue)

		const onChange = (e) => {
			if (initialValue !== e?.target?.value) {
				table.options.meta?.updateData(row.id, id, e?.target?.value)
			} else {
				table.options.meta?.updateData(row.id, id, null)
			}
			setValue(e?.target?.value)
		}

		useEffect(() => {
			setValue(table?.options?.meta?.changedValues?.[row.id]?.[id] ?? initialValue)
		}, [])

		useEffect(() => {
			if (!table.options.meta?.editMode) {
				setValue(initialValue)
			}
		}, [table?.options?.meta?.editMode])

		const editProps = columnDef?.editFn ? columnDef?.editFn(row.original) : columnDef?.edit;

		return table?.options?.meta?.editMode && columnDef?.editable ?
			(
				<EditCell
					{...editProps}
					columnId={id}
					row={row}
					value={value}
					onChange={onChange}
					onBlur={onChange}
				/>
			) : (
				<div style={{ ...columnDef?.meta?.style, overflow: "hidden", textOverflow: "ellipsis" }}>{initialValue}</div>
			)
	},
}

function useSkipper() {
	const shouldSkipRef = useRef(true)
	const shouldSkip = shouldSkipRef.current

	// Wrap a function with this to skip a pagination reset temporarily
	const skip = useCallback(() => {
		shouldSkipRef.current = false
	}, [])

	useEffect(() => {
		shouldSkipRef.current = true
	})

	return [shouldSkip, skip]
}
function NoRecords({ table }) {
	return <div className="data-table3__empty">
		<IconInformationCircle />
		No results found.
	</div>
}

NoRecords.propTypes = {
	table: PropTypes.object,
}

//un-memoized normal table body component - see memoized version below
function TableBody({ table, renderSubComponent }) {
	const { editMode, changedValues, onRowClick } = table?.options?.meta ?? { editMode: false, changedValues: {} }
	return (
		<div
			{...{
				className: "tbody"
			}}
		>
			{table?.getRowModel()?.rows?.length > 0 ?
				table.getRowModel().rows.map(row => (
					<Fragment
						key={row.id}
					>
						<div
							{...{
								"data-testid": 'data-table3-row',
								className: [
									"tr",
									editMode && changedValues?.[row.id] && Object.keys(changedValues?.[row.id])?.length > 0 ? 'data-table3__row--changed' : '',
									onRowClick ? 'data-table3__row--clickable' : ''
								].join(' '),
								onKeyDown: (e) => {
									e.stopPropagation();
									if (onRowClick) {
										onRowClick(row.id, row.original)
									}
								},
								onClick: (e) => {
									e.stopPropagation();
									if (onRowClick) {
										onRowClick(row.id, row.original)
									}
								}
							}}
						>
							{row.getVisibleCells().map(cell => {
								return (
									<div
										{...{
											key: cell.id,
											className: "td",
											style: {
												minWidth: `calc(var(--col-${cell.column.id}-size) * 1px)`,
												flex: `${cell.column.columnDef.flex} 1 0`,
											}
										}}
									>
										<div>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext()
											)}
										</div>
									</div>
								)
							})}
						</div>
						{row.getIsExpanded() && renderSubComponent && (
							<div className="tr">
								<div
									className="td"
									colSpan={row.getVisibleCells().length}
									style={{
										minWidth: `calc(var(--col-total-size) * 1px)`,
										width: '100%',
									}}
								>
									{renderSubComponent({ row })}
								</div>
							</div>
						)}
					</Fragment>
				)) :
				<NoRecords table={table} />
			}
		</div >
	)
}

TableBody.propTypes = {
	table: PropTypes.object,
	renderSubComponent: PropTypes.func,
}

//special memoized wrapper for our table body that we will use during column resizing
export const MemoizedTableBody = React.memo(
	TableBody,
	(prev, next) => prev.table.options.data === next.table.options.data
)

export const DataTable3 = React.forwardRef(({
	data, // data needs to be cached with react query
	columns,

	isEditable,
	onEditSave = (edited) => console.log('onEditSave'),
	showHeader,
	headerProps = {
		filter: {},
		setFilter: () => console.log('setFilter'),
		searchPlaceHolder: 'Search',
		showSearchInput: true,
		filterOptions: [],
	},
	toolbarActions, // custom action button
	onCancel = () => console.log('onCancel'), // extra function to execute onCancel
	onSave = () => console.log('onSave'), // extra function to execute onSave

	meta = {
		pagination: {
			count: 10, // total item
			currentPage: 1, // current page
			pageCount: 7, // total number of page
			pageSize: 5, // page size}
			setPageLimit: () => console.log('setPageLimit'),
			setCurrentPage: () => console.log('setCurrentPage'),
		},
	},
	enableMemo = true,
	showPageSize = false,
	pageSizeOptions = [5, 10, 20, 30, 40, 50],

	showInput,

	rowSelectionCondition = (row) => true, // row => row.role.some(role => role.name === 'admin'),
	rowSelectionIdentifier = 'id', // for other attributes, provide identifier attribute, row => row.<attribute>
	setSyncSelectedRow,

	rowCanExpandCondition = (row) => true, // row => row.role.some(role => role.name === 'admin'),
	renderSubComponent = ({ row }) => { return <div>Sub Component</div> },

	sortConfig,
	setSortConfig,

	setSyncChangedValues = () => { },

	isDirtyWarning = false, // show warning when user try to leave page with unsaved changes
	isLoading = false,

	showSelectedActions = false,
	selectedHeaderActions = <div>X item selected</div>,

	saveChangesBtnDisabled = false,
	onRowClick,

	...props
}, ref) => {
	const dispatch = useDispatch()
	const isDirty = useSelector(state => state?.isDirty)
	const [warningCount, setWarningCount] = useState(0)

	const [autoResetPageIndex, skipAutoResetPageIndex] = useSkipper()

	const [columnResizeMode, setColumnResizeMode] = useState('onChange')
	const [columnResizeDirection, setColumnResizeDirection] = useState('ltr')

	// track changedValues
	const [changedValues, setChangedValues] = useState({})

	useImperativeHandle(ref, () => ({
		changedValues,
		editMode,
		setEditMode,
		setRowSelection,
	}));

	useEffect(() => {
		if (!Array.isArray(data)) {
			throw Error('data must be array')
		}
		data.forEach((row) => {
			if (!row?.id) {
				throw Error('data must have an id')
			}
		});
	}, [data])

	useEffect(() => {
		if (!isDirtyWarning)
			return;
		const onBeforeUnload = (e) => {
			if (isDirty) {
				e.preventDefault();
				e.returnValue = "";
			}
		};
		window.addEventListener("beforeunload", onBeforeUnload);
		return () => {
			window.removeEventListener("beforeunload", onBeforeUnload);
		};
	}, [isDirty]);

	/**
	 * Pagination
	 */
	const [{ pageIndex, pageSize, pageCount, totalItem }, setPagination] = useState({
		pageIndex: (meta?.pagination?.currentPage ?? 1) - 1,
		pageSize: meta?.pagination?.pageSize ?? 5,
		pageCount: meta?.pagination?.pageCount ?? -1,
		totalItem: meta?.pagination?.count ?? 0,
	})
	const pagination = useMemo(() => ({
		pageIndex,
		pageSize,
		pageCount,
		totalItem,
	}), [pageIndex, pageSize, pageCount, totalItem])
	useEffect(() => {
		if (
			pagination.totalItem !== meta?.pagination?.count
			||
			pagination.pageCount !== meta?.pagination?.pageCount
		) {
			setPagination({
				...pagination,
				pageIndex: meta?.pagination?.currentPage - 1,
				pageCount: meta?.pagination?.pageCount,
				totalItem: meta?.pagination?.count,
			})
		}
	}, [meta?.pagination])
	useEffect(() => {
		if (pageIndex >= 0 && meta?.pagination?.setCurrentPage && pageIndex !== meta?.pagination?.currentPage - 1) {
			meta?.pagination?.setCurrentPage(pageIndex + 1);
		}
		if (pageSize > 0 && meta?.pagination?.setPageLimit && pageSize !== meta?.pagination?.pageSize) {
			meta?.pagination?.setPageLimit(pageSize);
		}
	}, [pageIndex, pageSize])

	function handlePageSizeChange(e) {
		setPagination({
			...pagination,
			pageIndex: 0,
			pageSize: Number(e.target.value),
		})

		return e;
	}

	/**
	 * Row Selection
	 */
	const [rowSelection, setRowSelection] = useState({})
	useEffect(() => {
		if (Object.keys(rowSelection)?.length > 0 && setSyncSelectedRow) {
			setSyncSelectedRow(Object.keys(rowSelection))
		}
	}, [rowSelection]);


	/**
	 * Edit
	 */
	const [editMode, setEditMode] = useState(false)

	useEffect(() => {
		setSyncChangedValues(changedValues)
		if (changedValues && Object.keys(changedValues)?.length && isDirtyWarning && !isDirty) {
			dispatch(setIsDirty(true))
		}
	}, [changedValues])

	/**
	 * Sorting
	 */
	const [sorting, setSorting] = useState([
		{
			id: sortConfig?.sortBy,
			desc: sortConfig?.direction !== 'asc',
		}
	])
	useEffect(() => {
		if (!setSortConfig)
			return;

		if (Array.isArray(sorting) && sorting.length > 0) {
			const sort = sorting[0]
			if (sortConfig?.sortBy !== sort?.id || sortConfig?.direction !== (sort?.desc ? 'desc' : 'asc')) {
				setSortConfig({
					sortBy: sort?.id,
					direction: !sort?.desc
				})
			}
		} else {
			if (sortConfig?.sortBy !== null || sortConfig?.direction !== false) {
				setSortConfig({
					sortBy: null,
					direction: false,
				})
			}
		}
	}, [sorting])

	const table = useReactTable({
		data,
		columns,
		defaultColumn,
		columnResizeMode,
		columnResizeDirection,

		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),

		state: {
			pagination,
			rowSelection,
			sorting,
		},


		manualPagination: true,
		pageCount: pagination.pageCount,
		onPaginationChange: setPagination,


		enableRowSelection: rowSelectionCondition,
		onRowSelectionChange: setRowSelection,
		getRowId: row => row?.[rowSelectionIdentifier],


		getExpandedRowModel: getExpandedRowModel(),
		getRowCanExpand: rowCanExpandCondition,


		onSortingChange: setSorting,
		manualSorting: true,


		// Provide our updateData function to our table meta
		meta: {
			updateData: (rowId, columnId, value) => {
				// Skip page index reset until after next rerender
				skipAutoResetPageIndex()
				const rowChangedValue = changedValues[rowId] ?? {}
				if (value === null) {
					delete rowChangedValue[columnId]
				} else {
					rowChangedValue[columnId] = value
				}
				setChangedValues({
					...changedValues,
					[rowId]: rowChangedValue
				})
			},
			editMode,
			changedValues,
			onRowClick,
		},

		// debugAll: true,
		// debugTable: true,
		// debugHeaders: true,
		// debugColumns: true,
	})

	table['getIsSomeRowsInPageSelected'] = () => {
		return (data?.some(row => rowSelection[row.id]) && !data?.every(row => rowSelection[row.id])) ?? false
	}

	/**
	 * Instead of calling `column.getSize()` on every render for every header
	 * and especially every data cell (very expensive),
	 * we will calculate all column sizes at once at the root table level in a useMemo
	 * and pass the column sizes down as CSS variables to the <table> element.
	 */
	const columnSizeVars = useMemo(() => {
		const headers = table.getFlatHeaders();
		const colSizes = {};
		for (const header of headers) {
			colSizes[`--header-${header.id}-size`] = header.getSize();
			colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
		}
		colSizes[`--col-total-size`] = table.getTotalSize();
		return colSizes;
	}, [table.getState().columnSizingInfo]);

	function showSaveButton(changedValues) {
		// if there are no changes, disable save button
		return Object.keys(changedValues)?.length === 0 ||
			(Object.keys(changedValues)?.length > 0 &&
				// if there are changes, check if there are any changes that are empty or null
				Object.values(changedValues)?.every((row) =>
					Object.entries(row)?.some(([key, value]) => {
						return Array.isArray(value) && value?.length === 0 || !value
					})
				)
			)
	}

	return (
		<div className="data-table3">
			<div className="data-table3__header">
				{showHeader &&
					<Toolbar {...{
						filter: headerProps?.filter,
						setFilter: headerProps?.setFilter,
						searchPlaceholder: headerProps?.searchPlaceholder,
						actions: (<div className="data_table__filter__action">
							{isEditable &&
								(!editMode ? (
									<Button
										data-testid="data-table3-edit"
										full
										round
										text="Edit Records"
										submit={() => {
											setEditMode(true)
											setChangedValues({})
											setWarningCount(0)
											dispatch(setIsDirty(false))
											setRowSelection({})
										}} />
								) : (
									<>
										<Button
											data-testid="data-table3-cancel"
											ghost
											round
											noborder
											text="Cancel"
											submit={() => {
												if (isDirtyWarning && isDirty && warningCount === 0) {
													alert('You have unsaved changes. Are you sure you want to cancel?')
													setWarningCount(1 + warningCount)
												} else {
													setEditMode(false)
													setChangedValues({})
													onCancel()
												}
											}} />
										<Button
											data-testid="data-table3-save"
											full
											round
											text="Save Changes"
											submit={() => {
												onEditSave(changedValues);
												onSave()
												setChangedValues({})
											}}
											disabled={saveChangesBtnDisabled || showSaveButton(changedValues)}
										/>
									</>
								)
								)
							}
							{!!toolbarActions && toolbarActions}
						</div>),
						showSearchInput: headerProps?.showSearchInput,
						filterOptions: headerProps?.filterOptions,

						rowSelection,
						showSelectedActions,
						selectedHeaderActions,
					}} />}
			</div>
			<div className="data-table3__body">
				<div
					{...{
						style: {
							...columnSizeVars, //Define column sizes on the <table> element
							minWidth: table.getTotalSize(),
							width: '100%',
						},
					}}
				>
					<div className="thead">
						{table.getHeaderGroups().map(headerGroup => (
							<div
								{...{
									key: headerGroup.id,
									className: 'tr',
								}}
							>
								{headerGroup.headers.map(header => (
									<div
										{...{
											key: header.id,
											className: 'th',
											style: {
												minWidth: `calc(var(--header-${header?.id}-size) * 1px)`,
												maxWidth: (header.column.getCanResize() || header.column.columnDef.flex) ? undefined : header.column.getSize(),
												flex: `${header.column.columnDef.flex} 1 0`,
											},
										}}
									>
										<div
											className={
												`data-table__table__head__title__sort-order ${{ 'asc': 'is-asc', 'desc': 'is-desc' }?.[header?.column?.getIsSorted()] ?? ''}`
											}
											onClick={header?.column?.columnDef?.sortable ? header.column.getToggleSortingHandler() : null}
											onKeyDown={header?.column?.columnDef?.sortable ? header.column.getToggleSortingHandler() : null}
										>
											{header.isPlaceholder
												? null
												:
												<>
													{flexRender(
														header.column.columnDef.header,
														header.getContext()
													)}
													{
														header?.column?.columnDef?.sortable &&
														<span className="data-table__table__head__icon__sort-order" data-testid="data-table3-sort" />
													}
												</>
											}
										</div>
										{header.column.getCanResize() && (
											<div
												{...{
													onDoubleClick: () => header.column.resetSize(),
													onMouseDown: header.getResizeHandler(),
													onTouchStart: header.getResizeHandler(),
													className: `resizer ${header.column.getIsResizing() ? 'isResizing' : ''}`,
												}}
											/>
										)}
									</div>
								))}
							</div>
						))}
					</div>

					{showInput && (
						<div className="data-table3__input tr">
							{table.getAllColumns().map(column => {
								return (
									<div
										{...{
											key: column.id + '-input',
											className: 'th',
											style: {
												minWidth: `calc(var(--header-${column?.id}-size) * 1px)`,
												flex: `${column.columnDef.flex} 1 0`,
											},
										}}
									>
										{column?.columnDef?.input?.render && column?.columnDef?.input?.render()}
									</div>
								)

							})}
						</div>
					)}


					{isLoading ?
						<div className="data-table__empty">
							<Loader
								className="loadingSpinner__position__center"
								data-testid="loadingSpinner__position__center"
								type="ThreeDots"
								color="grey"
								height={10}
								width={15}
							/>
						</div> :
						<>
							{/* When resizing any column we will render this special memoized version of our table body */}
							{table.getState().columnSizingInfo.isResizingColumn && enableMemo ? (
								<MemoizedTableBody table={table} renderSubComponent={renderSubComponent} />
							) : (
								<TableBody table={table} renderSubComponent={renderSubComponent} />
							)}
						</>
					}
				</div>
			</div>

			{/* Footer: Pagination */}
			{props?.debug &&
				<>{JSON.stringify(table.getState(), null, 2)}</>
			}
			<div className="data-table3__footer">
				<div className="data-table3__footer__left">
					Showing {(table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1) || 0} - {((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize) || 0}{pagination?.totalItem ? ` of ${pagination?.totalItem} entries` : ''}
				</div>
				<div className="data-table3__footer__right">
					<div className="data-table__table__pagination">
						<input
							data-testid="pagination-input"
							className="data-table__table__pagination__page"
							type="number"
							value={table.getState().pagination.pageIndex + 1}
							defaultValue={table.getState().pagination.pageIndex + 1}
							onChange={e => {
								const page = e.target.value ? Number(e.target.value) - 1 : 0
								table.setPageIndex(page)
							}}
							disabled={table.getState().pagination.pageCount === 0}
							min="1"
						/>
						<span className="data-table__table__pagination__page__meta"> of {table.getPageCount()} pages </span>
						<button data-testid="pagination-prev" className="data-table__table__pagination__prev" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}><IconChevronDown /></button>
						<button data-testid="pagination-next" className="data-table__table__pagination__next" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}><IconChevronDown /></button>

						{showPageSize && (
							<>
								<select
									data-testid="pagination-pagesize"
									className="data-table__table__pagination__page"
									type="number"
									value={table.getState().pagination.pageSize}
									onChange={handlePageSizeChange}
								>
									{pageSizeOptions.map(pageSize => (
										<option
											data-testid={`pagination-pagesize-option-${pageSize}`}
											key={pageSize}
											value={pageSize}
										>
											{pageSize}
										</option>
									))}
								</select> per page
							</>
						)}
					</div>
				</div>
			</div>
		</div >
	)
});

DataTable3.propTypes = {
	data: PropTypes.array.isRequired,
	columns: PropTypes.array.isRequired,

	isEditable: PropTypes.bool,
	onEditSave: PropTypes.func,
	showHeader: PropTypes.bool,
	headerProps: PropTypes.object,

	toolbarActions: PropTypes.node,
	onCancel: PropTypes.func,
	onSave: PropTypes.func,

	meta: PropTypes.objectOf({
		pagination: PropTypes.objectOf({
			count: PropTypes.number,
			currentPage: PropTypes.number,
			pageCount: PropTypes.number,
			pageSize: PropTypes.number,
			setPageLimit: PropTypes.func,
			setCurrentPage: PropTypes.func,
		}),
	}),
	enableMemo: PropTypes.bool,
	showPageSize: PropTypes.bool,
	pageSizeOptions: PropTypes.arrayOf(PropTypes.number),

	showInput: PropTypes.bool,

	rowSelectionCondition: PropTypes.func, // = (row) => true, // row => row.role.some(role => role.name === 'admin'),
	rowSelectionIdentifier: PropTypes.string, // for other attributes, provide identifier attribute, row => row.<attribute>
	setSyncSelectedRow: PropTypes.func,

	rowCanExpandCondition: PropTypes.func,// = (row) => true, // row => row.role.some(role => role.name === 'admin'),
	renderSubComponent: PropTypes.func,// = ({ row }) => { return <div>Sub Component</div> },

	sortConfig: PropTypes.object,
	setSortConfig: PropTypes.func,

	setSyncChangedValues: PropTypes.func,

	isDirtyWarning: PropTypes.bool, // = false, // show warning when user try to leave page with unsaved changes
	isLoading: PropTypes.bool,

	showSelectedActions: PropTypes.bool,
	selectedHeaderActions: PropTypes.node,

	saveChangesBtnDisabled: PropTypes.bool,
	onRowClick: PropTypes.func,

	debug: PropTypes.bool,
}
